import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Paginacion from './Paginacion';
import TablaMatriz from './TablaMatriz';

// Años y gestiones (igual patrón que los filtros de Tableros): la Gestión es un atajo
// que selecciona el set de años correspondiente.
const ALL_YEARS = ['2027','2026','2025','2024','2023','2022','2021','2020','2019','2018','2017','2016'];

const GESTIONES = [
	{ key: '2da', label: '2da Gestión (2024–2027)', years: ['2027','2026','2025','2024'] },
	{ key: '1era', label: '1era Gestión (2020–2023)', years: ['2023','2022','2021','2020'] },
];

const yearOptions = ALL_YEARS.map(y => ({ value: y, label: y }));
const gestionOptions = GESTIONES.map(g => ({ value: g.key, label: g.label }));

const TablaMatrizContainer = ({ datos = [], token }) => {
    const [datosOriginales, setDatosOriginales] = useState([]);
    const [datosFiltrados, setDatosFiltrados] = useState([]);
    const [guardandoCargado, setGuardandoCargado] = useState(null);

    const [filters, setFilters] = useState({
        solicitante: '',
        localizacion: '',
        fecha: '',
        year: [],
        gestion: [],
        search: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        setDatosOriginales(datos);
        setDatosFiltrados(datos);
        setCurrentPage(1);
    }, [datos]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Años únicos presentes en los datos (de fecha_firma_convenio)
    //
    // El año sale de los primeros 4 caracteres del string 'YYYY-MM-DD' y NO de
    // new Date(...).getFullYear(): el backend devuelve ese string, y Date lo
    // parsea como UTC midnight mientras getFullYear() devuelve el año LOCAL. En
    // Argentina (UTC-3) eso corre un día para atrás, así que un convenio del
    // 01/01/2026 se contaría como 2025. Hoy no hay ninguna fila en el límite
    // (la más cercana es del 02/01), pero el filtro por año es justamente lo que
    // se está arreglando, así que conviene que no dependa de la zona horaria.
    const aniosUnicos = [...new Set(
        datosOriginales
            .map(item => item.fecha_firma_convenio ? item.fecha_firma_convenio.slice(0, 4) : null)
            .filter(Boolean)
    )].sort((a, b) => b.localeCompare(a));

    const selectedYears = Array.isArray(filters.year) ? filters.year : [];

    const handleGestionChange = (selected) => {
        if (!selected || selected.length === 0) {
            setFilters(prev => ({ ...prev, gestion: [], year: [] }));
            return;
        }
        const gestiones = GESTIONES.filter(g => selected.some(o => o.value === g.key));
        const years = [...new Set(gestiones.flatMap(g => g.years))].sort((a, b) => b.localeCompare(a));
        setFilters(prev => ({ ...prev, gestion: selected.map(o => o.value), year: years }));
    };

    const handleYearsChange = (selected) => {
        if (!selected || selected.length === 0) {
            setFilters(prev => ({ ...prev, year: [] }));
        } else {
            setFilters(prev => ({ ...prev, year: selected.map(o => o.value) }));
        }
    };

    // Gestiones activas (completas dentro de la selección de años)
    const gestionesActivas = GESTIONES
        .filter(g => selectedYears.length > 0 && g.years.every(y => selectedYears.includes(y)))
        .map(g => g.key);

    // Año seleccionado como opciones react-select (limita a años con datos)
    const aniosConDatos = aniosUnicos.filter(a => ALL_YEARS.includes(a));
    const selectedYearOptions = aniosConDatos
        .filter(a => selectedYears.includes(a))
        .map(a => ({ value: a, label: a }));

    const handleGestionValue = () => {
        const active = Array.isArray(filters.gestion) ? filters.gestion : [];
        return gestionOptions.filter(o => active.includes(o.value) || gestionesActivas.includes(o.value));
    };

    const limpiarFiltros = () => {
        setFilters({ solicitante: '', localizacion: '', fecha: '', year: [], gestion: [], search: '' });
    };

    const toggleCargado = async (fila) => {
        const key = `${fila.id_obra}-${fila.tipo_registro}-${fila.orden}`;
        if (guardandoCargado === key) return;
        const nuevoValor = !fila.cargado;

        // Optimista: refleja el cambio en pantalla al instante
        setDatosOriginales(prev => prev.map(d =>
            d.id === fila.id ? { ...d, cargado: nuevoValor } : d
        ));
        setDatosFiltrados(prev => prev.map(d =>
            d.id === fila.id ? { ...d, cargado: nuevoValor } : d
        ));
        setGuardandoCargado(key);

        try {
            const res = await fetch('/api/base-matriz/cargado', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    id_obra: fila.id_obra,
                    tipo_registro: fila.tipo_registro,
                    orden: fila.orden || 0,
                    expediente: fila.expediente || null,
                    cargado: nuevoValor,
                }),
            });
            if (!res.ok) {
                // Revertir el cambio local si falla
                setDatosOriginales(prev => prev.map(d =>
                    d.id === fila.id ? { ...d, cargado: fila.cargado } : d
                ));
                setDatosFiltrados(prev => prev.map(d =>
                    d.id === fila.id ? { ...d, cargado: fila.cargado } : d
                ));
            }
        } catch (err) {
            console.error('[matriz] error al marcar cargado:', err);
            setDatosOriginales(prev => prev.map(d =>
                d.id === fila.id ? { ...d, cargado: fila.cargado } : d
            ));
            setDatosFiltrados(prev => prev.map(d =>
                d.id === fila.id ? { ...d, cargado: fila.cargado } : d
            ));
        } finally {
            setGuardandoCargado(null);
        }
    };

    useEffect(() => {
        let tempDatos = [...datosOriginales];

        if (filters.solicitante) {
            tempDatos = tempDatos.filter(item => item.solicitante === filters.solicitante);
        }
        if (filters.localizacion) {
            tempDatos = tempDatos.filter(item => item.localizacion === filters.localizacion);
        }
        if (filters.fecha) {
            tempDatos = tempDatos.filter(item => item.fecha === filters.fecha);
        }
        if (filters.year && filters.year.length > 0) {
            tempDatos = tempDatos.filter(item => {
                if (!item.fecha_firma_convenio) return false;
                // Ver la nota de 'aniosUnicos': el año se toma del string, no de
                // Date.getFullYear(), que corre un día para atrás en UTC-3.
                const anio = item.fecha_firma_convenio.slice(0, 4);
                return filters.year.includes(anio);
            });
        }
        if (filters.search) {
            const term = filters.search.toLowerCase();
            tempDatos = tempDatos.filter(item => {
                const solicitanteMatch = item.solicitante && item.solicitante.toLowerCase().includes(term);
                const expedienteMatch = item.expediente && item.expediente.toLowerCase().includes(term);
                return solicitanteMatch || expedienteMatch;
            });
        }

        setDatosFiltrados(tempDatos);
        setCurrentPage(1);
    }, [filters, datosOriginales]);

    const totalItems = datosFiltrados.length;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = datosFiltrados.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const solicitantesUnicos = [...new Set(datosOriginales.map(item => item.solicitante).filter(Boolean))];
    const localizacionesUnicas = [...new Set(datosOriginales.map(item => item.localizacion).filter(Boolean))];
    const fechasUnicas = [...new Set(datosOriginales.map(item => item.fecha).filter(Boolean))];

    const optionsSolicitantes = solicitantesUnicos.map(v => ({ value: v, label: v }));
    const optionsLocalizaciones = localizacionesUnicas.map(v => ({ value: v, label: v }));
    const optionsFechas = fechasUnicas.map(v => ({ value: v, label: v }));

    const handleSingleChange = (name) => (selected) => {
        setFilters(prev => ({ ...prev, [name]: selected ? selected.value : '' }));
    };

    const getSingleValue = (name, options) => {
        const val = filters[name];
        return (val && options.find(o => o.value === val)) || null;
    };

    return (
        <div className="w-full p-4">
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filtros</h3>
                    <button
                        onClick={limpiarFiltros}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md transition-colors"
                    >
                        Limpiar filtros
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 py-4">
                    <div>
                        <label htmlFor="solicitante" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Solicitante
                        </label>
                        <Select
                            options={optionsSolicitantes}
                            value={getSingleValue('solicitante', optionsSolicitantes)}
                            onChange={handleSingleChange('solicitante')}
                            isClearable
                            placeholder="Todos los solicitantes..."
                            className="text-sm"
                            styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                        />
                    </div>
                    <div>
                        <label htmlFor="localizacion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Localización
                        </label>
                        <Select
                            options={optionsLocalizaciones}
                            value={getSingleValue('localizacion', optionsLocalizaciones)}
                            onChange={handleSingleChange('localizacion')}
                            isClearable
                            placeholder="Todas las localizaciones..."
                            className="text-sm"
                            styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                        />
                    </div>
                    <div>
                        <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Año
                        </label>
                        <Select
                            isMulti
                            options={aniosConDatos.map(a => ({ value: a, label: a }))}
                            value={selectedYearOptions}
                            onChange={handleYearsChange}
                            isClearable
                            placeholder="Todos los años..."
                            closeMenuOnSelect={false}
                            className="text-sm"
                            styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                        />
                    </div>
                    <div>
                        <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fecha (mes)
                        </label>
                        <Select
                            options={optionsFechas}
                            value={getSingleValue('fecha', optionsFechas)}
                            onChange={handleSingleChange('fecha')}
                            isClearable
                            placeholder="Todos los meses..."
                            className="text-sm"
                            styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
                        />
                    </div>
                </div>

                {/* Fila Buscar debajo */}
                <div className="py-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                        <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                                placeholder="Buscar por solicitante o expediente..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TablaMatriz datos={currentItems} onToggleCargado={toggleCargado} guardandoCargado={guardandoCargado} />

            <Paginacion
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={totalItems}
            />
        </div>
    );
};

export default TablaMatrizContainer;
